/**
 * Question Bank Zustand Store
 * Manages UI state for Question Bank features
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { QuestionFilters, QuestionSortField, SortDirection } from '../utils/api/questionBankApi';

interface QuestionBankState {
  // Selection
  selectedQuestionIds: string[];
  setSelectedQuestionIds: (ids: string[]) => void;
  toggleQuestionSelection: (id: string) => void;
  selectAllQuestions: (ids: string[]) => void;
  clearSelection: () => void;
  isQuestionSelected: (id: string) => boolean;

  // Search & Filter
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filters: QuestionFilters;
  setFilters: (filters: QuestionFilters) => void;
  updateFilter: <K extends keyof QuestionFilters>(key: K, value: QuestionFilters[K]) => void;
  clearFilters: () => void;

  // Sort
  sortField: QuestionSortField;
  sortDirection: SortDirection;
  setSortField: (field: QuestionSortField) => void;
  setSortDirection: (direction: SortDirection) => void;
  toggleSortDirection: () => void;

  // View Mode
  viewMode: 'grid' | 'list' | 'table';
  setViewMode: (mode: 'grid' | 'list' | 'table') => void;

  // Wizard Builder State
  wizardBuilder: {
    selectedQuestions: string[];
    pages: Array<{
      id: string;
      title: string;
      description?: string;
      questionIds: string[];
    }>;
    currentPageIndex: number;
  };
  setWizardBuilderQuestions: (questionIds: string[]) => void;
  addQuestionToWizard: (questionId: string, pageIndex?: number) => void;
  removeQuestionFromWizard: (questionId: string, pageIndex: number) => void;
  addWizardPage: (page: { id: string; title: string; description?: string }) => void;
  removeWizardPage: (pageIndex: number) => void;
  updateWizardPage: (pageIndex: number, updates: Partial<{ title: string; description: string }>) => void;
  reorderWizardQuestions: (pageIndex: number, fromIndex: number, toIndex: number) => void;
  reorderWizardPages: (fromIndex: number, toIndex: number) => void;
  setCurrentWizardPage: (pageIndex: number) => void;
  resetWizardBuilder: () => void;

  // UI State
  isQuestionEditorOpen: boolean;
  editingQuestionId: string | null;
  openQuestionEditor: (questionId?: string) => void;
  closeQuestionEditor: () => void;

  isWizardBuilderOpen: boolean;
  editingWizardId: string | null;
  openWizardBuilder: (wizardId?: string) => void;
  closeWizardBuilder: () => void;

  isCategoryManagerOpen: boolean;
  openCategoryManager: () => void;
  closeCategoryManager: () => void;
}

const initialWizardBuilderState = {
  selectedQuestions: [],
  pages: [{
    id: 'page-1',
    title: 'Page 1',
    description: '',
    questionIds: [],
  }],
  currentPageIndex: 0,
};

export const useQuestionBankStore = create<QuestionBankState>()(
  persist(
    (set, get) => ({
      // Selection
      selectedQuestionIds: [],
      setSelectedQuestionIds: (ids) => set({ selectedQuestionIds: ids }),
      toggleQuestionSelection: (id) => set((state) => ({
        selectedQuestionIds: state.selectedQuestionIds.includes(id)
          ? state.selectedQuestionIds.filter(qId => qId !== id)
          : [...state.selectedQuestionIds, id],
      })),
      selectAllQuestions: (ids) => set({ selectedQuestionIds: ids }),
      clearSelection: () => set({ selectedQuestionIds: [] }),
      isQuestionSelected: (id) => get().selectedQuestionIds.includes(id),

      // Search & Filter
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),
      filters: {},
      setFilters: (filters) => set({ filters }),
      updateFilter: (key, value) => set((state) => ({
        filters: { ...state.filters, [key]: value },
      })),
      clearFilters: () => set({ filters: {}, searchQuery: '' }),

      // Sort
      sortField: 'category',
      sortDirection: 'asc',
      setSortField: (field) => set({ sortField: field }),
      setSortDirection: (direction) => set({ sortDirection: direction }),
      toggleSortDirection: () => set((state) => ({
        sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc',
      })),

      // View Mode
      viewMode: 'grid',
      setViewMode: (mode) => set({ viewMode: mode }),

      // Wizard Builder State
      wizardBuilder: initialWizardBuilderState,
      setWizardBuilderQuestions: (questionIds) => set((state) => ({
        wizardBuilder: {
          ...state.wizardBuilder,
          selectedQuestions: questionIds,
        },
      })),
      addQuestionToWizard: (questionId, pageIndex) => set((state) => {
        const targetPageIndex = pageIndex ?? state.wizardBuilder.currentPageIndex;
        const newPages = [...state.wizardBuilder.pages];
        if (!newPages[targetPageIndex].questionIds.includes(questionId)) {
          newPages[targetPageIndex] = {
            ...newPages[targetPageIndex],
            questionIds: [...newPages[targetPageIndex].questionIds, questionId],
          };
        }
        return {
          wizardBuilder: {
            ...state.wizardBuilder,
            pages: newPages,
          },
        };
      }),
      removeQuestionFromWizard: (questionId, pageIndex) => set((state) => {
        const newPages = [...state.wizardBuilder.pages];
        newPages[pageIndex] = {
          ...newPages[pageIndex],
          questionIds: newPages[pageIndex].questionIds.filter(id => id !== questionId),
        };
        return {
          wizardBuilder: {
            ...state.wizardBuilder,
            pages: newPages,
          },
        };
      }),
      addWizardPage: (page) => set((state) => ({
        wizardBuilder: {
          ...state.wizardBuilder,
          pages: [...state.wizardBuilder.pages, { ...page, questionIds: [] }],
        },
      })),
      removeWizardPage: (pageIndex) => set((state) => {
        if (state.wizardBuilder.pages.length <= 1) return state; // Keep at least one page
        const newPages = state.wizardBuilder.pages.filter((_, i) => i !== pageIndex);
        const newCurrentPageIndex = Math.min(state.wizardBuilder.currentPageIndex, newPages.length - 1);
        return {
          wizardBuilder: {
            ...state.wizardBuilder,
            pages: newPages,
            currentPageIndex: newCurrentPageIndex,
          },
        };
      }),
      updateWizardPage: (pageIndex, updates) => set((state) => {
        const newPages = [...state.wizardBuilder.pages];
        newPages[pageIndex] = {
          ...newPages[pageIndex],
          ...updates,
        };
        return {
          wizardBuilder: {
            ...state.wizardBuilder,
            pages: newPages,
          },
        };
      }),
      reorderWizardQuestions: (pageIndex, fromIndex, toIndex) => set((state) => {
        const newPages = [...state.wizardBuilder.pages];
        const questionIds = [...newPages[pageIndex].questionIds];
        const [removed] = questionIds.splice(fromIndex, 1);
        questionIds.splice(toIndex, 0, removed);
        newPages[pageIndex] = {
          ...newPages[pageIndex],
          questionIds,
        };
        return {
          wizardBuilder: {
            ...state.wizardBuilder,
            pages: newPages,
          },
        };
      }),
      reorderWizardPages: (fromIndex, toIndex) => set((state) => {
        const newPages = [...state.wizardBuilder.pages];
        const [removed] = newPages.splice(fromIndex, 1);
        newPages.splice(toIndex, 0, removed);
        return {
          wizardBuilder: {
            ...state.wizardBuilder,
            pages: newPages,
          },
        };
      }),
      setCurrentWizardPage: (pageIndex) => set((state) => ({
        wizardBuilder: {
          ...state.wizardBuilder,
          currentPageIndex: pageIndex,
        },
      })),
      resetWizardBuilder: () => set({
        wizardBuilder: initialWizardBuilderState,
      }),

      // UI State
      isQuestionEditorOpen: false,
      editingQuestionId: null,
      openQuestionEditor: (questionId) => set({
        isQuestionEditorOpen: true,
        editingQuestionId: questionId || null,
      }),
      closeQuestionEditor: () => set({
        isQuestionEditorOpen: false,
        editingQuestionId: null,
      }),

      isWizardBuilderOpen: false,
      editingWizardId: null,
      openWizardBuilder: (wizardId) => set({
        isWizardBuilderOpen: true,
        editingWizardId: wizardId || null,
      }),
      closeWizardBuilder: () => set({
        isWizardBuilderOpen: false,
        editingWizardId: null,
        wizardBuilder: initialWizardBuilderState,
      }),

      isCategoryManagerOpen: false,
      openCategoryManager: () => set({ isCategoryManagerOpen: true }),
      closeCategoryManager: () => set({ isCategoryManagerOpen: false }),
    }),
    {
      name: 'question-bank-storage',
      // Only persist certain fields
      partialize: (state) => ({
        viewMode: state.viewMode,
        sortField: state.sortField,
        sortDirection: state.sortDirection,
        filters: state.filters,
      }),
    }
  )
);
